import { debounce } from 'lodash-es';
import React, { createContext, useContext, useEffect, useState } from 'react';
import { BREAKPOINTS } from '#src/constants/breakpoints';

type BreakpointKey = keyof typeof BREAKPOINTS;

type BreakpointState = {
  [K in BreakpointKey]: {
    isAbove: boolean;
    isBelow: boolean;
  };
};

const BreakpointContext = createContext<BreakpointState | null>(null);

function getInitialState(): BreakpointState {
  if (typeof window === 'undefined') {
    return Object.keys(BREAKPOINTS).reduce((acc, key) => {
      acc[key as BreakpointKey] = { isAbove: false, isBelow: false };
      return acc;
    }, {} as BreakpointState);
  }

  return Object.entries(BREAKPOINTS).reduce((acc, [key, value]) => {
    acc[key as BreakpointKey] = {
      isAbove: window.matchMedia(`(min-width: ${value}px)`).matches,
      isBelow: window.matchMedia(`(max-width: ${value - 1}px)`).matches,
    };
    return acc;
  }, {} as BreakpointState);
}

export function BreakpointProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [breakpoints, setBreakpoints] =
    useState<BreakpointState>(getInitialState);

  useEffect(() => {
    const handlers = Object.entries(BREAKPOINTS).map(([key, value]) => {
      const above = window.matchMedia(`(min-width: ${value}px)`);
      const below = window.matchMedia(`(max-width: ${value - 1}px)`);
      const breakpointKey = key as BreakpointKey;
      const updateBreakpoint = debounce(() => {
        setBreakpoints((prev) => ({
          ...prev,
          [breakpointKey]: {
            isAbove: above.matches,
            isBelow: below.matches,
          },
        }));
      }, 100);

      above.addEventListener('change', updateBreakpoint);
      below.addEventListener('change', updateBreakpoint);

      return { above, below, handler: updateBreakpoint };
    });

    return () => {
      handlers.forEach(({ above, below, handler }) => {
        handler.cancel();
        above.removeEventListener('change', handler);
        below.removeEventListener('change', handler);
      });
    };
  }, []);

  return (
    <BreakpointContext.Provider value={breakpoints}>
      {children}
    </BreakpointContext.Provider>
  );
}

export function useBreakpointContext() {
  const context = useContext(BreakpointContext);
  if (!context) {
    throw new Error(
      'useBreakpointContext must be used within BreakpointProvider'
    );
  }
  return context;
}
